// PRE RMA — Hook des écrans de saisie : données locales d'abord (IndexedDB), rafraîchies par le serveur quand le réseau est là.
// Les tableaux calculés (totaux, reports, liens pré-inventaire ⇄ intrants ⇄ inventaire ⇄ finance) sont recalculés ICI avec la
// même fonction pure que le serveur : l'écran fonctionne donc entièrement hors connexion.
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  calculerModules, reportsDepuisSaisies, reportsVides, MEDICAMENTS_DEFAUT,
} from '../../../shared/business-logic/index.ts';
import type { MedicamentCatalogue } from '../../../shared/business-logic/index.ts';
import {
  chargerSnap, chargerParametres, chargerCatalogue, rafraichir, ecrire, supprimer, majParametres, majCatalogue, surChangement, synchroniser,
} from '../offline/dbModules.ts';
import type { Snap, ParametresLocaux } from '../offline/dbModules.ts';
import type { Periode } from '../pages/Accueil.tsx';
import { periodeVoisine } from '../offline/periodesCache.ts';

export function useModules(periode: Periode | null) {
  const [snap, setSnap] = useState<Snap | null>(null);
  const [params, setParams] = useState<ParametresLocaux | null>(null);
  const [catalogue, setCatalogue] = useState<MedicamentCatalogue[]>(MEDICAMENTS_DEFAUT);
  const [precSnap, setPrecSnap] = useState<Snap | null>(null);
  const [pret, setPret] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const recharger = useCallback(async () => {
    if (!periode) return;
    const s = await chargerSnap(periode.id);
    setSnap(s ?? null);
    setParams(await chargerParametres(periode.annee));
    const c = await chargerCatalogue();
    if (c) setCatalogue(c.medicaments);
    const prec = periodeVoisine(periode, -1);
    setPrecSnap(prec ? (await chargerSnap(prec.id)) ?? null : null);
    setPret(true);
  }, [periode?.id]);

  useEffect(() => {
    setPret(false); setSnap(null);
    if (!periode) return;
    let actif = true;
    void recharger().then(async () => {
      const s = await rafraichir(periode.id); // no-op hors connexion
      if (actif && !s) setErreur('Cette période n\'a pas encore été téléchargée sur cet appareil : ouvrez-la une première fois avec une connexion.');
      else if (actif) setErreur(null);
      if (actif) await recharger();
      void synchroniser();
    });
    const off = surChangement(() => { void recharger(); });
    return () => { actif = false; off(); };
  }, [periode?.id]);

  const lectureSeule = !periode || periode.statut === 'CLOTURE';

  // Reports : si le mois précédent est aussi sur cet appareil (peut-être saisi hors connexion), il est plus à jour que celui du serveur.
  const reports = useMemo(() => (precSnap ? reportsDepuisSaisies(precSnap.saisies, precSnap.reports ?? null) : (snap?.reports ?? reportsVides())), [precSnap, snap]);

  const calc = useMemo(() => {
    if (!snap || !periode) return null;
    const pevAnnee = snap.pevAnnee ?? Array(12).fill(null); // mois courant recalculé par calculerModules
    return calculerModules({ periode, saisies: snap.saisies, reports, parametres: { popTotale: params?.popTotale ?? null }, catalogue, pevAnnee });
  }, [snap, reports, params, catalogue, periode?.annee, periode?.mois]);

  const saisies = useCallback((tableau: string) => (snap?.saisies ?? []).filter((l) => l.tableau === tableau), [snap]);

  return {
    pret, erreur, calc, snap, reports, lectureSeule,
    popTotale: params?.popTotale ?? null, intrantsAjoutes: params?.intrantsAjoutes ?? [], catalogue,
    saisies,
    ecrire: (tableau: string, cle: string, donnees: any) => (periode && !lectureSeule ? ecrire(periode.id, tableau, cle, donnees) : Promise.resolve()),
    supprimer: (tableau: string, cle: string) => (periode && !lectureSeule ? supprimer(periode.id, tableau, cle) : Promise.resolve()),
    majParametres: (patch: Parameters<typeof majParametres>[1]) => (periode && !lectureSeule ? majParametres(periode.annee, patch) : Promise.resolve()),
    majCatalogue: (m: MedicamentCatalogue[]) => (lectureSeule ? Promise.resolve() : majCatalogue(m)),
  };
}
